import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Download, Search, X, BookOpen, Loader2, ArrowLeft } from 'lucide-react';

const BASE = '/livrets/am-8h-2026';
const TOTAL = 67;
const PDF_URL = `${BASE}/livret-am-topolino-2026.pdf`;
const pageUrl = (n) => `${BASE}/pages/p${n}.jpg`;

export default function LivretAmPage() {
  const [page, setPage] = useState(1);
  const [index, setIndex] = useState(null);
  const [q, setQ] = useState('');
  const [searchOpen, setSearchOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const imgRef = useRef(null);

  useEffect(() => {
    document.title = 'Livret AM Topolino — Mission 8 h (voiturette) | CodeVoiture.fr';
    fetch(`${BASE}/search-index.json`).then((r) => r.json()).then((d) => setIndex(d.pages || {})).catch(() => setIndex({}));
  }, []);

  const go = useCallback((n) => {
    const p = Math.min(TOTAL, Math.max(1, n));
    setPage(p); setLoading(true);
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      if (e.target.tagName === 'INPUT') return;
      if (e.key === 'ArrowRight') go(page + 1);
      if (e.key === 'ArrowLeft') go(page - 1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [page, go]);

  // préchargement des pages voisines
  useEffect(() => {
    [page + 1, page - 1, page + 2].forEach((n) => {
      if (n >= 1 && n <= TOTAL) { const im = new Image(); im.src = pageUrl(n); }
    });
  }, [page]);

  const results = useMemo(() => {
    if (!index || q.trim().length < 3) return [];
    const needle = q.trim().toLowerCase();
    const out = [];
    for (const [num, text] of Object.entries(index)) {
      const t = (text || '').toLowerCase();
      const i = t.indexOf(needle);
      if (i >= 0) {
        out.push({ page: Number(num), snippet: (text || '').slice(Math.max(0, i - 55), i + 85) });
        if (out.length >= 20) break;
      }
    }
    return out.sort((a, b) => a.page - b.page);
  }, [index, q]);

  return (
    <div className="min-h-screen bg-[#0a0a0b] text-white flex flex-col" data-testid="livret-am-page">
      {/* Barre supérieure */}
      <header className="sticky top-0 z-30 bg-[#0a0a0b]/95 backdrop-blur border-b border-white/10">
        <div className="max-w-6xl mx-auto px-3 sm:px-6 h-14 flex items-center gap-2 sm:gap-3">
          <Link to="/" className="inline-flex items-center gap-1.5 text-white/55 hover:text-white text-[12.5px] shrink-0" data-testid="livret-am-back">
            <ArrowLeft size={14} /> <span className="hidden sm:inline">codevoiture.fr</span>
          </Link>
          <div className="flex-1 min-w-0 text-center">
            <div className="text-[12px] sm:text-[13.5px] font-semibold truncate inline-flex items-center gap-1.5">
              <BookOpen size={13} className="text-amber-400 shrink-0" /> Livret AM Topolino · Mission 8 h <span className="hidden sm:inline text-white/40">· édition 2026</span>
            </div>
          </div>
          <button onClick={() => setSearchOpen((v) => !v)} data-testid="livret-am-search-toggle"
            className={`w-9 h-9 rounded-full inline-flex items-center justify-center shrink-0 transition ${searchOpen ? 'bg-amber-500 text-black' : 'bg-white/8 text-white/70 hover:text-white'}`}>
            {searchOpen ? <X size={15} /> : <Search size={15} />}
          </button>
          <a href={PDF_URL} download="Livret-AM-Topolino-Mission-8H.pdf" data-testid="livret-am-download"
            className="h-9 px-3 rounded-full bg-amber-500 hover:bg-amber-400 text-black text-[12px] font-bold inline-flex items-center gap-1.5 shrink-0 transition-colors">
            <Download size={13} /> <span className="hidden sm:inline">PDF</span>
          </a>
        </div>
        {/* Recherche plein texte */}
        {searchOpen && (
          <div className="border-t border-white/10 bg-[#0d0d0e]">
            <div className="max-w-6xl mx-auto px-3 sm:px-6 py-3">
              <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} data-testid="livret-am-search-input"
                placeholder="Rechercher dans les 67 pages… (3 lettres min)"
                className="w-full h-10 rounded-lg bg-white/[0.05] border border-white/15 focus:border-amber-500/60 outline-none px-3 text-[13.5px]" />
              {q.trim().length >= 3 && (
                <div className="mt-2 max-h-56 overflow-y-auto space-y-1" data-testid="livret-am-search-results">
                  {results.length === 0 && <div className="text-white/40 text-[12.5px] py-2">Aucun résultat.</div>}
                  {results.map((r) => (
                    <button key={r.page} onClick={() => { go(r.page); setSearchOpen(false); }}
                      className="w-full text-left rounded-lg border border-white/10 hover:border-amber-500/50 bg-white/[0.02] px-3 py-2 transition-colors">
                      <span className="text-amber-400 font-mono-spec text-[11px] font-bold">p. {r.page}</span>
                      <span className="text-white/60 text-[12px] ml-2">…{r.snippet}…</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </header>

      {/* Lecteur */}
      <main className="flex-1 flex flex-col items-center px-2 sm:px-6 py-4 sm:py-6">
        <div className="relative w-full max-w-[560px]">
          {loading && (
            <div className="absolute inset-0 flex items-center justify-center z-10"><Loader2 className="animate-spin text-amber-400" size={30} /></div>
          )}
          <img
            ref={imgRef}
            key={page}
            src={pageUrl(page)}
            alt={`Livret AM — page ${page}`}
            onLoad={() => setLoading(false)}
            data-testid="livret-am-page-img"
            className={`w-full rounded-xl border border-white/10 shadow-2xl transition-opacity duration-200 ${loading ? 'opacity-30' : 'opacity-100'}`}
          />
          {/* Zones cliquables gauche/droite */}
          <button aria-label="Page précédente" onClick={() => go(page - 1)} disabled={page <= 1} data-testid="livret-am-prev"
            className="absolute left-0 top-0 h-full w-1/4 flex items-center justify-start pl-1 opacity-0 hover:opacity-100 disabled:hidden transition-opacity">
            <span className="w-10 h-10 rounded-full bg-black/60 border border-white/20 flex items-center justify-center"><ChevronLeft size={18} /></span>
          </button>
          <button aria-label="Page suivante" onClick={() => go(page + 1)} disabled={page >= TOTAL} data-testid="livret-am-next"
            className="absolute right-0 top-0 h-full w-1/4 flex items-center justify-end pr-1 opacity-0 hover:opacity-100 disabled:hidden transition-opacity">
            <span className="w-10 h-10 rounded-full bg-black/60 border border-white/20 flex items-center justify-center"><ChevronRight size={18} /></span>
          </button>
        </div>

        {/* Contrôles */}
        <div className="mt-4 w-full max-w-[560px] flex items-center gap-2 sm:gap-3">
          <button onClick={() => go(page - 1)} disabled={page <= 1} data-testid="livret-am-prev-btn"
            className="h-10 px-3.5 rounded-full border border-white/15 disabled:opacity-30 hover:bg-white/5 inline-flex items-center gap-1 text-[12.5px] font-semibold transition-colors">
            <ChevronLeft size={14} /> <span className="hidden sm:inline">Préc.</span>
          </button>
          <input type="range" min={1} max={TOTAL} value={page} onChange={(e) => go(Number(e.target.value))} data-testid="livret-am-slider"
            className="flex-1 accent-amber-500" />
          <div className="shrink-0 text-[12.5px] font-mono-spec text-white/70 w-16 text-center" data-testid="livret-am-counter">{page} / {TOTAL}</div>
          <button onClick={() => go(page + 1)} disabled={page >= TOTAL} data-testid="livret-am-next-btn"
            className="h-10 px-3.5 rounded-full bg-amber-500 hover:bg-amber-400 text-black disabled:opacity-30 inline-flex items-center gap-1 text-[12.5px] font-bold transition-colors">
            <span className="hidden sm:inline">Suiv.</span> <ChevronRight size={14} />
          </button>
        </div>
        <p className="mt-3 text-white/35 text-[11.5px] text-center">← → au clavier · Livret pédagogique AM « Mission 8 h » (voiturette / quadricycle léger) · partagé avec code2roues.fr</p>
      </main>
    </div>
  );
}
